// Mapi Arcade: 20 quick mini-games built from LANDWAR's world and art.
// Each game has bronze/silver/gold score thresholds (20 × 3 = 60 medals) and a plausibility cap the server enforces.

export interface ArcadeMeta {
  id: string;
  icon: string;
  /** Score thresholds for bronze, silver, gold. */
  medals: [number, number, number];
  /** Highest score the server accepts (anti-cheat cap). */
  max: number;
  /** Upper bound on score gained per second of real play (plausibility check). */
  rate: number;
  /** Art used for the card (path under /art). */
  art: string;
}

export const ARCADE: ArcadeMeta[] = [
  { id: 'duel', icon: '⚔️', medals: [150, 300, 450], max: 1000, rate: 40, art: 'modes/arena.webp' },
  { id: 'slice', icon: '✂️', medals: [40, 60, 75], max: 100, rate: 4, art: 'realms/verdant/mural-2-sm.webp' },
  { id: 'hop', icon: '🪶', medals: [5, 15, 30], max: 500, rate: 1.6, art: 'realms/frost/mural-1-sm.webp' },
  { id: 'dodge', icon: '🫧', medals: [150, 400, 800], max: 20000, rate: 30, art: 'realms/neon/mural-1-sm.webp' },
  { id: 'coins', icon: '🪙', medals: [30, 80, 150], max: 3000, rate: 6, art: 'realms/ember/mural-1-sm.webp' },
  { id: 'snake', icon: '🐍', medals: [10, 25, 45], max: 500, rate: 1.6, art: 'realms/verdant/mural-1-sm.webp' },
  { id: 'stack', icon: '🧱', medals: [8, 20, 35], max: 500, rate: 1.6, art: 'realms/tidal/mural-1-sm.webp' },
  { id: 'memory', icon: '🖼️', medals: [300, 600, 900], max: 1500, rate: 40, art: 'realms/astral/mural-1-sm.webp' },
  { id: 'bomb', icon: '💣', medals: [3, 7, 10], max: 200, rate: 1, art: 'realms/ember/mural-2-sm.webp' },
  { id: 'whack', icon: '🔨', medals: [20, 40, 65], max: 400, rate: 9, art: 'realms/tidal/mural-2-sm.webp' },
  { id: 'runner', icon: '🏃', medals: [300, 800, 1500], max: 100000, rate: 160, art: 'realms/neon/mural-2-sm.webp' },
  { id: 'breakout', icon: '🛡️', medals: [20, 45, 70], max: 3000, rate: 5, art: 'realms/frost/mural-2-sm.webp' },
  { id: 'compass', icon: '🧭', medals: [5, 12, 20], max: 300, rate: 2.2, art: 'realms/astral/mural-2-sm.webp' },
  { id: 'flood', icon: '🎨', medals: [10, 30, 60], max: 1000, rate: 6, art: 'realms/verdant/mural-3-sm.webp' },
  { id: 'slide', icon: '🧊', medals: [3, 6, 10], max: 200, rate: 0.6, art: 'realms/frost/mural-3-sm.webp' },
  { id: 'freeze', icon: '❄️', medals: [15, 35, 60], max: 2000, rate: 5, art: 'realms/frost/mural-4-sm.webp' },
  { id: 'lantern', icon: '🏮', medals: [200, 500, 1000], max: 100000, rate: 80, art: 'realms/neon/mural-3-sm.webp' },
  { id: 'orbit', icon: '🪐', medals: [5, 12, 25], max: 1000, rate: 1.2, art: 'realms/astral/mural-3-sm.webp' },
  { id: 'rhythm', icon: '🎵', medals: [1500, 4000, 7000], max: 12000, rate: 220, art: 'realms/tidal/mural-3-sm.webp' },
  { id: 'merge', icon: '🫠', medals: [1000, 4000, 10000], max: 500000, rate: 450, art: 'realms/ember/mural-3-sm.webp' },
];

export const ARCADE_IDS = ARCADE.map((g) => g.id);

export function arcadeMeta(id: string): ArcadeMeta | undefined {
  return ARCADE.find((g) => g.id === id);
}

/** 0 = none, 1 = bronze, 2 = silver, 3 = gold. */
export function medalFor(id: string, score: number): number {
  const m = arcadeMeta(id);
  if (!m) return 0;
  return score >= m.medals[2] ? 3 : score >= m.medals[1] ? 2 : score >= m.medals[0] ? 1 : 0;
}

/** Coins awarded when a medal tier is reached for the first time (cumulative per tier). */
export const MEDAL_COINS = [0, 15, 30, 60];

/** Three games featured today (same for everyone). */
export function dailyArcade(day: string): string[] {
  let h = 2166136261;
  for (const c of day) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
  const picks: string[] = [];
  let i = 0;
  while (picks.length < 3) {
    const id = ARCADE_IDS[(h + i * 7919) % ARCADE_IDS.length];
    if (!picks.includes(id)) picks.push(id);
    i++;
  }
  return picks;
}
